import React, { useEffect, useMemo, useState } from "react";
import { NO_PROJECT, useTasks } from "../../contexts/tasks.context";
import { createCn } from "../../helpers/utils";
import classes from './styles.module.scss';
import { useTranslation } from "react-i18next";
import { TaskItem, TaskStatus } from "../../services/api.service";
import ProjectSelect from "./components/project-select.component";
import TaskColumn from "./components/column.component";
import { toast } from "react-toastify";
import {
  DragDropContext,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";

const cn = createCn("tasks", classes);

/** Колонки доски фиксированные — по статусам задач бэкенда. */
export const BOARD_COLUMNS: { status: TaskStatus; titleKey: string }[] = [
  { status: 'todo', titleKey: 'tasks-board.col-todo' },
  { status: 'in_progress', titleKey: 'tasks-board.col-in-progress' },
  { status: 'review', titleKey: 'tasks-board.col-review' },
  { status: 'done', titleKey: 'tasks-board.col-done' },
];

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

const TasksPage = () => {
  const { t } = useTranslation();
  const {
    tasks,
    projects,
    loading,
    error,
    scope,
    setScope,
    moveTask,
    reorderColumn,
  } = useTasks();
  const [date, setDate] = useState<Date | null>(null);
  const [query, setQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  // Автовыбор первой группы; сброс, если выбранный проект исчез из данных.
  useEffect(() => {
    if (selectedProject && !projects.includes(selectedProject)) {
      setSelectedProject(projects[0] ?? null);
      return;
    }
    if (!selectedProject && projects.length) {
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject]);

  const projectTasks = useMemo(() => {
    if (!selectedProject) return [];
    return tasks.filter(task => (task.project || NO_PROJECT) === selectedProject);
  }, [tasks, selectedProject]);

  const columnTasks = useMemo(() => {
    const byStatus = new Map<TaskStatus, TaskItem[]>();
    BOARD_COLUMNS.forEach(col => {
      byStatus.set(
        col.status,
        projectTasks
          .filter(task => task.status === col.status)
          .sort((a, b) => a.position - b.position || a.id - b.id),
      );
    });
    return byStatus;
  }, [projectTasks]);

  const matchesFilters = (task: TaskItem): boolean => {
    if (query && !task.title.toLowerCase().includes(query.toLowerCase())) {
      return false;
    }
    if (date) {
      if (!task.due_date) return false;
      const due = new Date(task.due_date);
      if (Number.isNaN(due.getTime()) || !sameDay(due, date)) return false;
    }
    return true;
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const taskId = Number(draggableId);
    const fromStatus = source.droppableId as TaskStatus;
    const toStatus = destination.droppableId as TaskStatus;

    if (fromStatus === toStatus) {
      // Внутри колонки — reorder по полному списку id колонки.
      const ids = (columnTasks.get(fromStatus) || []).map(task => task.id);
      const [moved] = ids.splice(source.index, 1);
      ids.splice(destination.index, 0, moved);
      reorderColumn(ids).catch(e => {
        toast.error(e instanceof Error ? e.message : t('tasks-board.move-error'));
      });
      return;
    }

    moveTask(taskId, toStatus, destination.index).catch(e => {
      toast.error(e instanceof Error ? e.message : t('tasks-board.move-error'));
    });
  };

  return (
    <div className={cn()}>
      <div className={cn("heading")}>
        {t('tasks.heading')}
      </div>

      <div className={cn("tabs")}>
        {(['all', 'mine'] as const).map(value => (
          <div
            key={value}
            className={cn("tab", { active: scope === value })}
            onClick={() => setScope(value)}
          >
            {t(`tasks-board.scope-${value}`)}
          </div>
        ))}
      </div>

      <div className={cn("filters")}>
        <ProjectSelect
          projects={projects}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
        />

        <div className={cn('filter')}>
          <label htmlFor="date">{t('tasks.due-date')}</label>
          <input
            type="date"
            id="date"
            value={date ? date.toISOString().split('T')[0] : ''}
            onChange={(e) => setDate(e.target.value ? new Date(e.target.value) : null)}
          />
        </div>

        <div className={cn('filter')}>
          <label htmlFor="name"></label>
          <input
            type="text"
            id="name"
            placeholder={t('files.name')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {loading && <p className={cn("status-msg")}>{t('tasks-board.loading')}</p>}
      {!loading && error && <p className={cn("status-msg")}>{error}</p>}
      {!loading && !error && !projects.length && (
        <p className={cn("status-msg")}>{t('tasks-board.empty')}</p>
      )}

      {!loading && !error && selectedProject && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className={cn("columns")}>
            {BOARD_COLUMNS.map(col => {
              const all = columnTasks.get(col.status) || [];
              const visible = all.filter(matchesFilters);
              return (
                <Droppable droppableId={col.status} key={col.status}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <TaskColumn
                        status={col.status}
                        title={t(col.titleKey)}
                        tasks={visible}
                        project={selectedProject}
                      />
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      )}
    </div>
  );
};

export default TasksPage;
